import React, { useState, useEffect } from 'react';
import { Platform, Pressable, Image, View, StyleSheet } from 'react-native';
import { useThemeName, Text } from '@tamagui/core';
import { Home, MessageCircle, Plus, User, Settings } from 'lucide-react-native';
import { COLORS } from '@/constants/colors';
import { navigationRef } from '@/navigation/navigationRef';
import { useChats } from '@/hooks/useFirebaseChat';
import { useApplicationNotifications } from '@/hooks/useApplicationNotifications';

// @ts-ignore
import logoImage from '@/assets/logo.png';

const navItems = [
  { route: 'Home', tabName: 'Home', icon: Home, label: 'Home' },
  { route: 'Chat', tabName: 'Chat', icon: MessageCircle, label: 'Chat' },
  { route: 'Create', tabName: 'Create', icon: Plus, label: 'Create' },
  { route: 'Profile', tabName: 'Profile', icon: User, label: 'Profile' },
  { route: 'Settings', tabName: 'Settings', icon: Settings, label: 'Settings' },
];

type WebHeaderProps = {
  hideNavigation?: boolean;
};

export function WebHeader({ hideNavigation = false }: WebHeaderProps) {
  const [currentRoute, setCurrentRoute] = useState('Home');
  const themeName = useThemeName();
  const isDark = themeName === 'dark';
  
  // Get unread chat count
  const { totalUnread } = useChats();
  
  // Get new applications count
  const { totalNewCount: newApplicationsCount } = useApplicationNotifications();

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // Listen for navigation state changes
    const unsubscribe = navigationRef.addListener('state', () => {
      try {
        const state = navigationRef.getState();
        if (!state?.routes || state.index === undefined) return;
        
        const route = state.routes[state.index];
        if (!route) return;
        
        if (route.name === 'TabNavigator' && route.state?.routes && route.state.index !== undefined) {
          const tabRoute = route.state.routes[route.state.index];
          setCurrentRoute(tabRoute?.name || 'Home');
        } else if (route.name) {
          setCurrentRoute(route.name);
        }
      } catch (e) {
        // Ignore navigation state errors
      }
    });

    return unsubscribe;
  }, []);

  if (Platform.OS !== 'web') {
    return null;
  }

  const isActive = (tabName: string) => {
    // Special handling for Create button - it navigates to CreateAnnouncement screen
    if (tabName === 'Create') {
      return currentRoute === 'CreateAnnouncement';
    }
    // Special handling for Profile button - it navigates to MyProfile screen
    if (tabName === 'Profile') {
      return currentRoute === 'MyProfile';
    }
    return currentRoute === tabName;
  };

  const handleNavPress = (tabName: string) => {
    if (navigationRef.isReady()) {
      // Special handling for Create button - navigate to CreateAnnouncement screen
      if (tabName === 'Create') {
        // @ts-ignore
        navigationRef.navigate('CreateAnnouncement');
      } else if (tabName === 'Profile') {
        // Profile button navigates to MyProfile screen (Mój profil)
        // @ts-ignore
        navigationRef.navigate('MyProfile');
      } else {
        // @ts-ignore
        navigationRef.navigate('TabNavigator', { screen: tabName });
      }
    }
  };

  const handleLogoPress = () => {
    handleNavPress('Home');
  };


  // Theme-aware colors
  const headerBg = isDark ? 'rgba(24, 24, 27, 0.95)' : 'rgba(255, 255, 255, 0.95)';
  const iconColor = isDark ? '#a1a1aa' : '#4A5568';
  const activeIconColor = '#B9849D';

  return (
    <header style={{ ...styles.header, backgroundColor: headerBg }}>
      <div style={styles.container}>
        {/* Logo */}
        <Pressable onPress={handleLogoPress} style={styles.logoLink}>
          <div style={styles.logoContainer}>
            <Image 
              source={logoImage} 
              style={{ width: 40, height: 40 }} 
              resizeMode="contain"
            />
            <span style={{ 
              ...styles.logoText, 
              background: 'linear-gradient(to right, #9C5879, #6EC5DB)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>TINO</span>
          </div>
        </Pressable>

        {/* Navigation - Centered (hidden on auth screens) */}
        {!hideNavigation && (
          <nav style={styles.nav}>
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.tabName);
              
              return (
                <Pressable
                  key={item.route}
                  onPress={() => handleNavPress(item.tabName)}
                  style={{
                    ...styles.navItem,
                    ...(active ? { backgroundColor: isDark ? '#B9849D30' : '#B9849D20' } : {}),
                  }}
                  accessibilityLabel={item.label}
                >
                  <View>
                    <Icon
                      size={22}
                      color={active ? activeIconColor : iconColor}
                      strokeWidth={active ? 2.5 : 2}
                    />
                    {item.tabName === 'Chat' && totalUnread > 0 && (
                      <View style={badgeStyles.badge}>
                        <Text color="white" fontSize={10} fontWeight="700">
                          {totalUnread > 99 ? '99+' : totalUnread}
                        </Text>
                      </View>
                    )}
                    {item.tabName === 'Settings' && newApplicationsCount > 0 && (
                      <View style={badgeStyles.badge}>
                        <Text color="white" fontSize={10} fontWeight="700">
                          {newApplicationsCount > 99 ? '99+' : newApplicationsCount}
                        </Text>
                      </View>
                    )}
                  </View>
                </Pressable>
              );
            })}
          </nav>
        )}

        {/* Right side action
        <Pressable onPress={handleMyAnnouncementsPress} style={styles.rightAction} accessibilityLabel="Moje ogłoszenia">
          <FileText size={22} color={iconColor} />
        </Pressable> */}
      </div>
    </header>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
    width: '100%',
    flexShrink: 0,
    minHeight: 60,
    display: 'flex',
    alignItems: 'center',
  },
  container: {
    maxWidth: 1400,
    margin: '0 auto',
    padding: '0 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  logoLink: {
    cursor: 'pointer',
    position: 'absolute',
    left: 32,
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  logoText: {
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: 2,
  },
  nav: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 24,
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent',
    cursor: 'pointer',
  },
  navItemSpecial: {
    backgroundColor: COLORS.PRIMARY,
    width: 52,
    height: 52,
    borderRadius: 26,
    boxShadow: '0 4px 16px rgba(94, 128, 156, 0.4)',
  },
  rightAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 48,
    height: 48,
    borderRadius: 24,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
};

const badgeStyles = StyleSheet.create({
  badge: {
    position: 'absolute',
    top: -4,
    right: -8,
    backgroundColor: COLORS.ERROR,
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
});
